import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axios';
import { getRole } from '../../utils/role';


// FETCH USERS
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async ({ page = 1, limit = 15, keyword, source }, { rejectWithValue }) => {
    try {
      const role = getRole();

      // SUPPORT
      if (role === 'SUPPORT') {
        const query = new URLSearchParams({
          page,
          limit,
          ...(keyword && { keyword }),
          ...(source && { source }),
        }).toString();

        const { data } = await axiosInstance.get(`/super-admin/users?${query}`);
        return { mode: 'SUPPORT', data };
      }

      // FIELD_OFFICER
      if (role === 'FIELD_OFFICER') {
        const { data } = await axiosInstance.get('/field-officer/farmers');
        return { mode: 'FIELD_OFFICER', data: data.result };
      }

      // PARTNER
      const { data } = await axiosInstance.get('/admin/users');
      return { mode: 'PARTNER', data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.msg || 'Failed to fetch users');
    }
  }
);

//  ADD FARMER
export const addFarmer = createAsyncThunk(
  'users/addFarmer',
  async (payload, { rejectWithValue }) => {
    try {
      const role = getRole();

      // SUPPORT
      if (role === 'SUPPORT') {
        const { data } = await axiosInstance.post('/super-admin/add-user', {
          data: payload,
        });
        return data;
      }

      // FIELD_OFFICER
      if (role === 'FIELD_OFFICER') {
        const { data } = await axiosInstance.post('/field-officer/add-farmer', {
           ...payload,
        });
        return data;
      }

      // PARTNER
      const { data } = await axiosInstance.post('/admin/user', {
        data: payload,
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.msg || 'Failed to add farmer');
    }
  }
);

const initialState = {
  users: [],       
  allUsers: [],    
  
  source: '',
  searchKeyword: '',
  
  currentPage: 1,
  totalPages: 1,

  status: 'idle',
  addFarmerStatus: 'idle',
  error: null,
};

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    setSource(state, action) {
      state.source = action.payload;
    },
    setSearchKeyword(state, action) {
      state.searchKeyword = action.payload;
    },
    setTotalPages(state, action) {
      state.totalPages = action.payload;
    },
    resetAddFarmerStatus(state) {
      state.addFarmerStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      /* FETCH USERS */
      .addCase(fetchUsers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const { mode, data } = action.payload;

        if (mode === 'SUPPORT') {
          state.users = data.users || [];
          state.totalPages = Math.ceil((data.totalCount || 0) / 15);
        } else {
          state.allUsers = data || [];
          state.users = data || []; 
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      /* ADD FARMER */
      .addCase(addFarmer.pending, (state) => {
        state.addFarmerStatus = 'loading';
        state.error = null;
      })
      .addCase(addFarmer.fulfilled, (state) => {
        state.addFarmerStatus = 'succeeded';
        state.error = null;
      })
      .addCase(addFarmer.rejected, (state, action) => {
        state.addFarmerStatus = 'failed';
        state.error = action.payload;
      });
  },
});

export const { 
  setCurrentPage, 
  setSource, 
  setSearchKeyword, 
  setTotalPages,
  resetAddFarmerStatus,
} = usersSlice.actions;

export default usersSlice.reducer;